import { useEffect, useState } from 'react';
import {
  actions,
  type ActionAssignee,
  type ActionTarget,
  type AssignableSet,
} from '../api/actions';
import { describeError, useTranslate } from '../i18n/react';
import { Button } from '../ui/Button';
import { Drawer } from '../ui/Drawer';
import { Alert, Field, Select, Textarea, TextInput } from '../ui/Field';

/**
 * Raising an action on a finding, from the visit it was found in.
 *
 * <p>Where the job can go depends on what kind of job it is. A free one closes on the
 * owner's word and may go to the store or to head office, so the panel asks which. An
 * approved one travels the chain the checklist lays down, and that chain is the store's
 * own — so it goes to the store, and the question is not asked.</p>
 *
 * <p>Either way the choice is between a seat and a person. A seat outlives whoever sits in
 * it, which is why it is offered first and why the hint says so.</p>
 */
export function RaiseAction({
  auditId,
  questionId,
  questionText,
  comment,
  onClose,
  onRaised,
}: {
  auditId: string;
  questionId: string;
  questionText: string;
  /** The note the auditor wrote on the question; it opens the "what was found" field. */
  comment?: string;
  onClose: () => void;
  onRaised: () => void;
}) {
  const t = useTranslate();

  const [who, setWho] = useState<AssignableSet | null>(null);
  const [picked, setPicked] = useState('');
  const [kind, setKind] = useState<'Free' | 'Approved'>('Free');
  const [target, setTarget] = useState<ActionTarget>('store');
  const [problem, setProblem] = useState(() => comment?.trim() ?? '');
  const [dueOn, setDueOn] = useState(() => {
    // Two weeks is a working default, not a rule: the auditor moves it.
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  });

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;

    void (async () => {
      try {
        const set = await actions.assignable(auditId);
        if (alive) setWho(set);
      } catch (e) {
        if (alive) setError(describeError(e, 'error.listFailed'));
      }
    })();

    return () => {
      alive = false;
    };
  }, [auditId]);

  // The option value carries the kind as well as the id, because the same identifier means
  // a different thing under each one.
  const parse = (value: string): { kind: ActionAssignee; id: string } | null => {
    const [kind, id] = value.split(':');
    return kind && id ? { kind: kind as ActionAssignee, id } : null;
  };

  async function raise() {
    const assignee = parse(picked);
    if (!assignee || busy) return;

    setBusy(true);
    try {
      await actions.raise(auditId, {
        questionId,
        kind,
        assignee: assignee.kind,
        assigneeId: assignee.id,
        problem: problem.trim(),
        dueOn,
      });

      onRaised();
      onClose();
    } catch (e) {
      setError(describeError(e, 'error.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  // An approved action is the store's business whatever the target says, so the question is
  // not asked and the answer is not read.
  const where = kind === 'Approved' ? 'store' : target;
  const group = who ? who[where] : null;

  const groups = group
    ? [
        { label: t('raise.seats'), items: group.seats },
        { label: t('raise.people'), items: group.people },
      ].filter((g) => g.items.length > 0)
    : [];

  // A pick belongs to the list it came from. Leaving it behind when the list changes sends
  // somebody the new list does not contain — which the server refuses, correctly, with a
  // message about a screen the auditor is no longer looking at.
  function choose(next: ActionTarget) {
    setTarget(next);
    setPicked('');
  }

  function chooseKind(next: 'Free' | 'Approved') {
    setKind(next);
    setPicked('');
  }

  return (
    <Drawer
      open
      title={t('raise.title')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!picked || !problem.trim() || !dueOn || busy}
            loading={busy}
            loadingText={t('common.saving')}
            onClick={() => void raise()}
          >
            {t('raise.confirm')}
          </Button>
        </>
      }
    >
      <Field label={t('actions.finding')}>{() => <p>{questionText}</p>}</Field>

      <Field label={t('actions.problem')} required hint={t('raise.problemHint')}>
        {({ id, describedBy }) => (
          <Textarea
            id={id}
            aria-describedby={describedBy}
            placeholder={t('raise.problemPlaceholder')}
            value={problem}
            onChange={(e) => setProblem(e.target.value)}
          />
        )}
      </Field>

      {/* Offered only when the checklist actually describes a chain: an approved action
          with nobody to approve it would be answered and then stuck for ever. */}
      {who?.hasApprovalChain ? (
        <Field label={t('raise.kind')} hint={t('raise.kindHint')}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={kind}
              onChange={(e) => chooseKind(e.target.value as 'Free' | 'Approved')}
            >
              <option value="Free">{t('actions.kind.Free')}</option>
              <option value="Approved">{t('actions.kind.Approved')}</option>
            </Select>
          )}
        </Field>
      ) : null}

      {/* Only a free action has somewhere else to go. */}
      {kind === 'Free' ? (
        <Field label={t('raise.target')} hint={t('raise.targetHint')}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={target}
              onChange={(e) => choose(e.target.value as ActionTarget)}
            >
              {/* Head office twice, because the two answer different questions: the
                  department for the ordinary case, the named person for a department that
                  serves head office and the stores at once. */}
              <option value="headOfficeDepartments" disabled={who?.hasHeadOfficeDepartments === false}>
                {t('raise.targetDepartments')}
                {who?.hasHeadOfficeDepartments === false ? ` — ${t('raise.noHeadOffice')}` : ''}
              </option>
              <option value="headOfficePeople" disabled={who?.hasHeadOfficePeople === false}>
                {t('raise.targetPeople')}
                {who?.hasHeadOfficePeople === false ? ` — ${t('raise.noHeadOfficePeople')}` : ''}
              </option>
              <option value="store">{t('raise.targetStore')}</option>
            </Select>
          )}
        </Field>
      ) : null}

      <Field label={t('actions.assignee')} required hint={t('raise.assigneeHint')}>
        {({ id, describedBy }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            value={picked}
            onChange={(e) => setPicked(e.target.value)}
          >
            <option value="">{t('common.none')}</option>
            {/* An empty list has a cause the auditor can do nothing about from here, so it
                says which one rather than looking like a list that failed to load. */}
            {who && groups.length === 0 ? (
              <option value="" disabled>
                {where === 'headOfficeDepartments' && !who.hasHeadOfficeDepartments
                  ? t('raise.noHeadOffice')
                  : where === 'headOfficePeople' && !who.hasHeadOfficePeople
                    ? t('raise.noHeadOfficePeople')
                    : t('raise.nobodySeated')}
              </option>
            ) : null}
            {groups.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.items.map((x) => (
                  // Disabled rather than hidden when the candidate cannot answer: the seat
                  // somebody expects to pick is still shown, with the reason beside it,
                  // instead of quietly missing from a list they know it belongs in.
                  <option
                    key={`${x.kind}:${x.id}`}
                    value={`${x.kind}:${x.id}`}
                    disabled={x.canAnswer === false}
                  >
                    {x.detail ? `${x.name} · ${x.detail}` : x.name}
                    {x.canAnswer === false ? ` — ${t('actions.cannotAnswer')}` : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        )}
      </Field>

      <Field label={t('actions.due')} required hint={t('raise.dueHint')}>
        {({ id, describedBy }) => (
          <TextInput
            id={id}
            aria-describedby={describedBy}
            type="date"
            value={dueOn}
            onChange={(e) => setDueOn(e.target.value)}
          />
        )}
      </Field>

      {error ? <Alert>{error}</Alert> : null}
    </Drawer>
  );
}
